/**
 * glRecovery — recuperação de contexto WebGL para os renderers AUXILIARES (os retratos
 * de conquista: banner do topo, chips do rodapé e o retrato estático de fallback).
 *
 * O QUE O THREE JÁ FAZ SOZINHO (r169, conferido em WebGLRenderer): registra os próprios
 * listeners no canvas, dá `preventDefault()` no 'webglcontextlost', torna `render()` um
 * no-op seguro enquanto o contexto está perdido e refaz o estado GL no restore. Ou seja:
 * a perda NÃO derruba o loop nem vira permanente por conta própria.
 *
 * O QUE O THREE NÃO FAZ — e é o motivo real deste módulo:
 *   • parar o TRABALHO do subsistema: sem o flag `_lost`, compose + Box3 + física do
 *     confete + drawImage continuavam rodando todo quadro por cima de um render que
 *     não desenha nada;
 *   • proteger os canvases 2D dos chips: `drawImage` de um canvas WebGL perdido pinta
 *     transparente — cada quadro APAGAVA o chip, que ficava em branco;
 *   • proteger o `toDataURL`: num contexto perdido ele não lança, devolve um PNG em
 *     branco que viraria um <img> vazio permanente;
 *   • assentar a UI: celebrações interrompidas ficariam congeladas no meio do pulo
 *     (ver `_abortAll` — o dono encerra o que estava no ar e o Hud troca o visual).
 *
 * Num telão que roda 8 h a perda acontece de verdade: driver atualizado, máquina
 * suspensa, GPU reset. O renderer PRINCIPAL tem tratamento próprio e mais agressivo
 * (SceneManager._initContextRecovery: para o loop, avisa na tela, recarrega em 8 s).
 * Aqui é deliberadamente mais simples porque retrato de conquista é decorativo — a
 * cidade continua rodando e o subsistema volta sozinho no restore.
 *
 * O `preventDefault()` abaixo é redundante com o do three enquanto o renderer existir,
 * mas fica: é o requisito da PLATAFORMA para o browser disparar o restore, e não deve
 * depender de detalhe interno de uma versão do three.
 *
 * Este módulo é separado (em vez de morar em AvatarPortrait.js) porque Achievements.js
 * e AvatarPortrait.js já se importam mutuamente — as peças do avatar vêm de um e o
 * rasterizador do outro. Pendurar o helper em qualquer um dos dois fecharia o ciclo.
 */

/**
 * @param {HTMLCanvasElement} canvas  o canvas do renderer auxiliar
 * @param {object} dono               objeto que ganha o flag `_lost`; se tiver `_abortAll()`,
 *                                    é chamado na perda para encerrar o que estava no ar
 * @param {Function} [aoRestaurar]    reaplica tamanho/pixelRatio quando o contexto volta
 */
export function attachContextRecovery(canvas, dono, aoRestaurar) {
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    dono._lost = true;
    dono._lostAt = performance.now();
    dono._abortAll?.();
  }, false);
  canvas.addEventListener('webglcontextrestored', () => {
    dono._lost = false;
    dono._lostAt = 0;
    try {
      aoRestaurar?.();
    } catch (err) {
      console.warn('retrato de conquista: falha ao restaurar o contexto WebGL', err);
    }
  }, false);
}

/** Quanto esperar pelo 'webglcontextrestored' antes de dar o contexto por perdido. */
export const RESTORE_TIMEOUT_MS = 20000;

/**
 * O restore não veio — e não virá.
 *
 * Os retratos usam canvases OFFSCREEN (nunca inseridos no DOM: o do chip é copiado com
 * drawImage, o estático vira dataURL). O 'webglcontextrestored' é disparado pelo browser
 * para canvases que ele PRECISA voltar a compor; num canvas fora do documento ele pode
 * simplesmente nunca chegar. Como `_lost` bloqueia todo o subsistema, uma perda dessas —
 * um GPU reset qualquer, que num telão de 8 h acontece — deixaria as conquistas do resto
 * do evento como bolinhas de cor, sem nenhum caminho de volta.
 *
 * Passado o prazo, o dono descarta renderer e canvas e recomeça do zero. Um canvas que
 * perdeu o contexto não devolve outro, então RECRIAR o canvas é a única saída real.
 */
export function desistiuDoRestore(dono, agora = performance.now()) {
  return !!dono._lost && !!dono._lostAt && agora - dono._lostAt >= RESTORE_TIMEOUT_MS;
}
